/**
 * MIRROR OF THE SERVER — hand-edited, NOT generated.
 *
 * Source of truth: `server/src/domain/schemas/realtime/realtime.schema.ts`
 * (re-exported for the wire by `server/src/api/subscriptions/subscriptions.contract.ts`).
 *
 * Realtime/SSE events are NOT part of the REST OpenAPI document, so
 * `openapi:gen` does not regenerate this file, and `sync:contracts` must NOT be
 * run against it (it is destructive — it would clobber the hand-written
 * mirror). Adding or widening a realtime event means editing the server schema
 * and mirroring it HERE by hand; a non-`rag_progress` event additionally needs
 * `realtime.contract.ts` updated (`REALTIME_EVENT_TYPES` +
 * `REALTIME_PAYLOAD_SCHEMAS` — both or neither, a bare string is a compile
 * error). `rag_progress` is deliberately absent from that pair.
 *
 * This file exists so the web SPA can import zod schemas + oRPC contract types
 * from `@event-manager/api-client/contracts/<domain>.contract` without reaching
 * into the server workspace.
 */

/**
 * Subscriptions Contract - oRPC Migration
 *
 * This contract defines realtime event stream schemas for SSE/WebSocket subscriptions.
 * These schemas are used with eventIterator in oRPC procedures to provide type-safe
 * realtime event streams.
 *
 * Event Categories:
 * - Calendar Events: event_created, event_updated, event_cancelled, event_reinstated, event_completed, event_no_show
 * - Conversation Events: new_message, message_sync, cleared, typing, stream events
 * - User Events: document_updated, favorite_updated, active_updated, name_updated
 * - System Events: config_updated, service_status, metrics_updated
 */

import { eventIterator, oc } from "@orpc/contract";
import * as z from "zod";
import { AgentWorkflowStateSchema } from "./agent-workflow.contract";
import { PhoneE164Schema } from "./base";
import { SubscriptionStatusSchema } from "./billing.contract";
import { ReminderSnapshotSchema } from "./conversations.contract";

// =============================================================================
// Common Schemas
// =============================================================================

/** Notification source for tracking operation origin */
export const NotificationSourceSchema = z.enum([
	"local",
	"agent",
	"external_user",
	"system_agent",
]);

/** Message role enum */
export const MessageRoleSchema = z.enum([
	"user",
	"assistant",
	"secretary",
	"admin",
	"tool",
	"system",
]);

// =============================================================================
// Calendar Event Payload Schemas
// =============================================================================

/**
 * Base calendar event data - shared across event types.
 *
 * This mirror is what `useRealtimeSubscription` / `useCalendarSubscription`
 * validate every SSE frame with, and Zod hands the parsed (key-STRIPPED) object
 * to the cache sync — so a key the server stamps but this schema omits never
 * reaches any client code. Keep it in step with the server's
 * `domain/schemas/realtime/realtime.schema.ts` and the sibling mirror in
 * `realtime.contract.ts`.
 */
const CalendarEventBaseSchema = z.object({
	contact_id: z.string().min(1).optional(),
	id: z.union([z.number(), z.string()]).optional(),
	event_id: z.union([z.number(), z.string()]).optional(),
	phone_e164: PhoneE164Schema.optional(),
	user_name: z.string().optional(),
	eventDateTime: z.string().datetime(),
	// Exclusive end of the booking's interval. Undeclared, it was stripped from
	// every live frame: a multi-slot booking arrived on the calendar as one slot
	// on its start day and only got its real length back on the next refetch.
	endsAt: z.string().datetime().optional(),
	type: z.string().optional(),
	cancelled: z.boolean().optional(),
	source: NotificationSourceSchema.optional(),
	_source: z.string().optional(),
	/** Acting org member's user id (absent for AI / external / system flows). */
	actor_user_id: z.string().nullish(),
	/** Acting org member's display label (email today). */
	actor_name: z.string().nullish(),
	status: z.string().optional(),
});

/** Event created payload - when a new calendar event is scheduled */
export const EventCreatedPayloadSchema = CalendarEventBaseSchema;

/** Event updated payload - when an existing event is modified */
export const EventUpdatedPayloadSchema = CalendarEventBaseSchema.extend({
	original_data: CalendarEventBaseSchema.omit({
		source: true,
		_source: true,
	}).optional(),
});

/** Event cancelled payload - when an event is cancelled */
export const EventCancelledPayloadSchema = CalendarEventBaseSchema.extend({
	has_document: z.boolean().optional(),
});

/** Event reinstated payload - when a cancelled event is reinstated */
export const EventReinstatedPayloadSchema = CalendarEventBaseSchema.extend({
	has_document: z.boolean().optional(),
});

/** Event completed payload - when an event is marked completed */
export const EventCompletedPayloadSchema = CalendarEventBaseSchema.extend({
	status: z.string().optional(),
	has_document: z.boolean().optional(),
});

/** Event no-show payload - when an event is marked as a no-show */
export const EventNoShowPayloadSchema = CalendarEventBaseSchema.extend({
	status: z.string().optional(),
	has_document: z.boolean().optional(),
});

// =============================================================================
// Conversation Message Payload Schemas
// =============================================================================

/** Base message data - shared across message event types */
const MessageBaseSchema = z.object({
	message_id: z.string(),
	id: z.string().optional(),
	phone_e164: PhoneE164Schema.optional(),
	user_id: z.string().min(1).optional(),
	conversationId: z.string().min(1).optional(),
	// Channel-agnostic contact identity — how the projection routes a phone-less
	// channel to its cached conversation. Undeclared here it was stripped from
	// every live row while a refetched row carried it.
	contactId: z.string().min(1).optional(),
	channel: z
		.enum([
			"whatsapp",
			"sms",
			"email",
			"telegram",
			"instagram",
			"x",
			"messenger",
			"system_agent",
		])
		.optional(),
	user_name: z.string().optional(),
	role: MessageRoleSchema,
	message: z.string(),
	date: z.string(),
	time: z.string(),
	sentAt: z.string().optional(),
	parts: z.array(z.unknown()).optional(),
	toolInvocations: z.array(z.unknown()).optional(),
	reasoning: z.string().optional(),
	// Quoted-reply block (WhatsApp-style) carried on new-message / sync events so
	// a live message renders its quote without a refetch. Mirrors ReplyToSchema.
	replyTo: z
		.object({
			messageId: z.string().nullish(),
			externalMessageId: z.string().nullish(),
			preview: z.string(),
			role: z.string().nullish(),
		})
		.nullish(),
	// Platform message id of THIS row (WhatsApp wamid). The web react
	// affordance is gated on its presence, so live rows must carry it.
	externalMessageId: z.string().optional(),
	reactions: z
		.array(
			z.object({
				emoji: z.string().min(1),
				direction: z.enum(["INBOUND", "OUTBOUND"]),
				sentAt: z.string(),
				externalMessageId: z.string().nullish(),
			})
		)
		.max(2)
		.nullish(),
	deliveryStatus: z
		.enum(["pending", "sending", "sent", "delivered", "read", "failed"])
		.nullish(),
	buttonReply: z
		.object({
			id: z.string(),
			title: z.string(),
			kind: z.enum([
				"interactive_button",
				"interactive_list",
				"template_button",
			]),
			contextExternalMessageId: z.string().nullish(),
			inReplyToMessageId: z.string().nullish(),
			description: z.string().nullish(),
		})
		.nullish(),
	interactiveButtons: z
		.object({
			bodyText: z.string(),
			buttons: z
				.array(z.object({ id: z.string(), title: z.string() }))
				.min(1)
				.max(3),
			headerText: z.string().nullish(),
			footerText: z.string().nullish(),
		})
		.nullish(),
	providerMetadata: z.record(z.string(), z.unknown()).nullish(),
	reminder: ReminderSnapshotSchema.nullish(),
	telegram: z.record(z.string(), z.unknown()).optional(),
	instagram: z.record(z.string(), z.unknown()).optional(),
	x: z.record(z.string(), z.unknown()).optional(),
	// Soft-delete timestamp of THIS row. A sync carrying it removes the message
	// from the chat cache instead of upserting it, so it must survive the parse.
	deletedAt: z.string().nullish(),
	_source: z.string().optional(),
});

/** New message received from user - triggers notifications */
export const ConversationNewMessagePayloadSchema = MessageBaseSchema;

/** Message sync for secretary messages - UI sync only, no notifications */
export const ConversationMessageSyncPayloadSchema = MessageBaseSchema;

/**
 * Per-operator conversation read cursor. Delivered only on the acting user's
 * private `/all` channel so their other tabs reconcile unread state.
 */
export const ConversationReadPayloadSchema = z.object({
	orgId: z.string().min(1),
	userId: z.string().min(1),
	conversationId: z.string().regex(/^\d+$/),
	lastReadAt: z.string().datetime(),
});

/**
 * Conversation cleared payload.
 *
 * A phone-addressed clear (WhatsApp) carries `phone_e164`; a by-conversation-id
 * clear (Instagram/Telegram/X/Messenger) carries `conversationId` + `channel`
 * instead, since those channels have no phone alias. Both keys are optional so
 * either path validates, and the web projection routes by whichever is present.
 */
export const ConversationClearedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema.optional(),
	conversationId: z.string().min(1).optional(),
	channel: z.string().min(1).optional(),
	user_id: z.string().optional(),
	deleted: z.number().optional(),
	_source: z.string().optional(),
});

/** A single reaction entry as carried on a conversation_message_edited event. */
export const ConversationReactionSchema = z.object({
	emoji: z.string(),
	direction: z.enum(["INBOUND", "OUTBOUND"]),
	sentAt: z.string(),
	externalMessageId: z.string().optional(),
});

/**
 * Conversation message edited payload - for edit/regenerate flows AND reaction
 * upserts. A reaction change reuses this event: `id`/`reactions` carry the
 * updated row so the web side merges by id (mirrors the message-recorded
 * broadcast shape); `role`/`message`/`sentAt` echo the target for convenience.
 */
export const ConversationMessageEditedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	from_message_id: z.string(),
	deleted: z.number().optional(),
	edited_content: z.string().optional(),
	// Reaction-upsert fields (absent on edit/regenerate broadcasts).
	id: z.string().optional(),
	message_id: z.string().optional(),
	reactions: z.array(ConversationReactionSchema).optional(),
	role: z.string().optional(),
	message: z.string().optional(),
	sentAt: z.string().optional(),
	_source: z.string().optional(),
});

/**
 * Delivery-status receipt for an OUTBOUND (bot/operator) message. Carries the
 * WhatsApp provider receipt (sent -> delivered -> read, or failed) so the web
 * side advances the tick indicator on the target row (matched by id, falling
 * back to externalMessageId) without a refetch. Absent on inbound turns.
 */
export const ConversationMessageStatusPayloadSchema = z.object({
	phone_e164: PhoneE164Schema.optional(),
	user_id: z.string().min(1).optional(),
	channel: z.string().min(1).optional(),
	id: z.string().optional(),
	message_id: z.string().optional(),
	conversationId: z.string().optional(),
	externalMessageId: z.string().optional(),
	deliveryStatus: z.enum([
		"pending",
		"sending",
		"sent",
		"delivered",
		"read",
		"failed",
	]),
	_source: z.string().optional(),
});

/**
 * A single message row was removed — soft-deleted locally by an operator, or
 * revoked by its sender on the platform. Carries whichever identifier the
 * deleting path knows (`id`/`message_id` for a local row, `externalMessageId`
 * for a provider revoke) plus the conversation identity, so a phone-less
 * channel still routes. `deletedAt` is the soft-delete timestamp.
 */
export const ConversationMessageDeletedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema.optional(),
	user_id: z.string().min(1).optional(),
	contactId: z.string().min(1).optional(),
	conversationId: z.string().min(1).optional(),
	channel: z.string().min(1).optional(),
	id: z.string().optional(),
	message_id: z.string().optional(),
	externalMessageId: z.string().optional(),
	deletedAt: z.string().nullish(),
	_source: z.string().optional(),
});

/** Typing indicator payload for phone-based and platform-scoped channels. */
export const ConversationTypingPayloadSchema = z.union([
	z.object({
		phone_e164: PhoneE164Schema,
		state: z.enum(["start", "stop"]),
		_source: z.string().optional(),
	}),
	z.object({
		channel: z.string().min(1),
		channelAddress: z.string().min(1),
		contactId: z.string().min(1),
		conversationId: z.string().min(1).optional(),
		orgId: z.string().min(1),
		state: z.enum(["start", "stop"]),
		timestamp: z.string().optional(),
		_source: z.string().optional(),
	}),
]);

/**
 * AI auto-reply enabled/disabled for a conversation.
 *
 * Fired when the AI calls the handoffToHuman tool, when an operator toggles
 * the switch in the UI, or any other code path that flips conversation.aiEnabled.
 * Frontend uses this to invalidate the contact list (so the bg tint + brain-icon
 * indicator appear immediately) and to surface a high-visibility notification.
 *
 * `phone_e164` is `z.string()` (not the strict E.164 schema) because messenger
 * contacts may have synthetic `messenger:<psid>` identifiers when no real phone
 * is on file. This event is a UI-state sync, not a phone-routed message — the
 * frontend uses the value purely as a cache key, so any stable contact
 * identifier is acceptable.
 */
export const ConversationAiToggledPayloadSchema = z.object({
	phone_e164: z.string().min(1),
	contact_id: z.string().optional(),
	user_name: z.string().optional(),
	channel: z.string(),
	ai_enabled: z.boolean(),
	/** Reason supplied by the AI when handing off; absent for operator toggles. */
	reason: z.string().optional(),
	_source: z.string().optional(),
});

/**
 * A conversation's pending-tool-approval state changed: the customer assistant
 * suspended a `needsApproval` tool call (true), or the operator resolved the
 * last pending one (false). Same `phone_e164` relaxation rationale as
 * ConversationAiToggledPayloadSchema — the value is a cache key, not a route.
 */
export const ConversationToolApprovalPendingPayloadSchema = z.object({
	phone_e164: z.string().min(1),
	contact_id: z.string().nullish(),
	channel: z.string().nullish(),
	has_pending_approval: z.boolean(),
	_source: z.string().optional(),
});

/**
 * Customer feedback recorded by the AI via the saveFeedback tool.
 *
 * Same `phone_e164` relaxation rationale as ConversationAiToggledPayload —
 * messenger contacts may not have an E.164 identifier.
 */
export const ConversationFeedbackPayloadSchema = z.object({
	phone_e164: z.string().min(1),
	contact_id: z.string().optional(),
	user_name: z.string().optional(),
	channel: z.string(),
	/** AI summary in the operator's configured language. */
	summary: z.string(),
	/** Verbatim quote from the customer (their actual words). */
	quote: z.string(),
	sentiment: z.enum(["positive", "negative", "neutral"]),
	_source: z.string().optional(),
});

// =============================================================================
// Streaming Event Payload Schemas
// =============================================================================

/** Stream start - signals the start of a streaming response */
export const ConversationStreamStartPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	stream_id: z.string(),
	role: z.literal("assistant"),
	_source: z.string().optional(),
	// For tool approval continuations - tells ALL clients to append to existing message
	is_continuation: z.boolean().optional(),
	continuation_message_id: z.string().optional(),
	continuation_parts: z.array(z.unknown()).optional(),
});

/** Stream chunk - contains a chunk of streaming text */
export const ConversationStreamChunkPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	stream_id: z.string(),
	chunk: z.string(),
	_source: z.string().optional(),
});

/** Stream SSE - SSE-formatted streaming data (unified with System Agent) */
export const ConversationStreamSsePayloadSchema = z.object({
	phoneE164: PhoneE164Schema,
	streamId: z.string(),
	chunk: z.string(),
	/** Monotonically increasing sequence number for dedup after snapshot catch-up */
	seq: z.number().int().optional(),
	_source: z.string().optional(),
});

/** Stream reasoning - contains reasoning delta text */
export const ConversationStreamReasoningPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	stream_id: z.string(),
	reasoning: z.string(),
	_source: z.string().optional(),
});

/**
 * Agent-workflow progress — the multi-agent RAG pipeline (modes 4/5) emits the
 * full tracker state on every step transition so watchers render a live
 * progress tracker while the AI works. `phone_e164` is the conversation route
 * key (relaxed: non-phone channels use a synthetic key, mirroring the server's
 * StreamingConversationKeySchema).
 */
export const ConversationWorkflowProgressPayloadSchema = z.object({
	phone_e164: z.string().min(1),
	workflow: AgentWorkflowStateSchema,
	_source: z.string().optional(),
});
export type ConversationWorkflowProgressPayload = z.infer<
	typeof ConversationWorkflowProgressPayloadSchema
>;

/** AI usage cost data for stream end events */
export const AiUsageCostDataSchema = z
	.object({
		promptTokens: z.number().optional(),
		completionTokens: z.number().optional(),
		totalTokens: z.number().optional(),
		cost: z.number().optional(),
	})
	.passthrough();

/** Stream end - signals the end of a streaming response */
export const ConversationStreamEndPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	stream_id: z.string(),
	role: z.literal("assistant"),
	message: z.string(),
	date: z.string(),
	time: z.string(),
	message_id: z.string().optional(),
	reasoning: z.string().optional(),
	usage: AiUsageCostDataSchema.optional(),
	_source: z.string().optional(),
});

/** Tool call started - signals a tool is being called */
export const ConversationToolCallStartedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	stream_id: z.string(),
	tool_call_id: z.string(),
	tool_name: z.string(),
	tool_args: z.record(z.string(), z.unknown()).optional(),
	_source: z.string().optional(),
});

/** Tool call completed - signals a tool call has finished */
export const ConversationToolCallCompletedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	stream_id: z.string(),
	tool_call_id: z.string(),
	tool_name: z.string(),
	tool_result: z.unknown().optional(),
	tool_error: z.string().optional(),
	_source: z.string().optional(),
});

/** Tool approval requested - signals a tool requires approval before execution */
export const ConversationToolApprovalRequestedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	stream_id: z.string(),
	tool_call_id: z.string(),
	tool_name: z.string(),
	approval_id: z.string(),
	tool_args: z.record(z.string(), z.unknown()).optional(),
	_source: z.string().optional(),
});

/** Tool approval responded - signals a tool approval was approved or denied */
export const ConversationToolApprovalRespondedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	message_id: z.string(),
	tool_call_id: z.string(),
	tool_name: z.string(),
	approved: z.boolean(),
	_source: z.string().optional(),
});

/** Stream snapshot - full accumulated state for mid-stream catch-up */
export const ConversationStreamSnapshotPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	stream_id: z.string(),
	message_id: z.string(),
	parts: z.array(z.object({ type: z.string() }).passthrough()),
	/** Sequence number of last chunk included in the snapshot */
	seq: z.number().int(),
	_source: z.string().optional(),
});

// =============================================================================
// User Event Payload Schemas
// =============================================================================

/**
 * User document updated payload.
 *
 * `phone_e164` is nullable and `contact_id` optional because a beneficiary is
 * phoneless by definition — its document save is addressed by contact id.
 */
export const UserDocumentUpdatedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema.nullish(),
	contact_id: z.string().min(1).optional(),
	has_document: z.boolean(),
	_source: z.string().optional(),
});

/** User favorite updated payload */
export const UserFavoriteUpdatedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	favorite: z.boolean().optional(),
	_source: z.string().optional(),
});

/** User active updated payload */
export const UserActiveUpdatedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	active: z.boolean().optional(),
	_source: z.string().optional(),
});

/** User name updated payload */
export const UserNameUpdatedPayloadSchema = z.object({
	phone_e164: PhoneE164Schema,
	user_name: z.string(),
	_source: z.string().optional(),
});

/** User phone changed payload */
export const UserPhoneChangedPayloadSchema = z.object({
	old_phone_e164: PhoneE164Schema,
	new_phone_e164: PhoneE164Schema,
	user_name: z.string().optional(),
	event_id: z.union([z.number(), z.string()]).optional(),
	_source: z.string().optional(),
});

/** Tenant-wide contact consolidation projection. */
export const ContactMergedPayloadSchema = z.object({
	orgId: z.string().min(1),
	loser_contact_id: z.string().min(1),
	winner_contact_id: z.string().min(1),
	winner_phone_e164: PhoneE164Schema.optional(),
	contact_id: z.string().min(1).optional(),
	_source: z.string().optional(),
	actor_user_id: z.string().optional(),
	actor_name: z.string().optional(),
});

/**
 * Identity carried by every contact-row mutation broadcast. Tenant-wide like
 * `contact_merged`, and self-sufficient so a client patches its cached row
 * without refetching the list. `phone_e164` is nullable — non-phone channels
 * (Instagram/Telegram/X/Messenger) have no phone alias.
 */
const ContactMutationBaseSchema = z.object({
	orgId: z.string().min(1),
	contact_id: z.string().min(1),
	phone_e164: PhoneE164Schema.nullish(),
	name: z.string().nullish(),
	channel: z.string().min(1).nullish(),
	_source: z.string().optional(),
	actor_user_id: z.string().optional(),
	actor_name: z.string().optional(),
});

/** A contact row was created. */
export const ContactCreatedPayloadSchema = ContactMutationBaseSchema;

/** An existing contact row's editable fields changed. */
export const ContactUpdatedPayloadSchema = ContactMutationBaseSchema;

/**
 * A contact row was soft-deleted. The resulting soft-delete state travels on
 * the wire so the client flips the row in place.
 */
export const ContactDeletedPayloadSchema = ContactMutationBaseSchema.extend({
	deleted_at: z.string().nullish(),
	is_active: z.boolean().optional(),
});

/** A soft-deleted contact row was restored (`deleted_at` back to null). */
export const ContactRestoredPayloadSchema = ContactMutationBaseSchema.extend({
	deleted_at: z.string().nullish(),
	is_active: z.boolean().optional(),
});

// =============================================================================
// System Event Payload Schemas
// =============================================================================

/** Config updated payload */
export const ConfigUpdatedPayloadSchema = z.object({
	updated_at: z.string().optional(),
	sections: z.array(z.string()).optional(),
	// The id of the client that initiated the write, echoed back so that client
	// can ignore its own realtime echo. The server stamps it on every org config
	// broadcast; undeclared here it was stripped, and the web echo-guard fell
	// back to a blanket time window that also swallowed OTHER members' changes.
	origin_client_id: z.string().nullish(),
	_source: z.string().optional(),
});

/** Agent preset created, edited, removed, activated, or duplicated. */
export const AiPresetUpdatedPayloadSchema = z.object({
	operation: z.enum([
		"created",
		"updated",
		"deleted",
		"activated",
		"duplicated",
		// A channel was pinned to (or unpinned from) this preset. Distinct from
		// 'activated': the org default is unchanged, only one channel's override.
		"bound",
	]),
	presetId: z.string(),
	presetName: z.string(),
	activePresetId: z.string(),
});

/** Vacation period updated payload */
export const VacationPeriodUpdatedPayloadSchema = z.object({
	periods: z.array(z.unknown()).optional(),
	// Which mutation this broadcast represents, so the activity feed reads
	// "Vacation added/updated/removed" instead of a single "updated".
	operation: z.enum(["added", "updated", "deleted"]).optional(),
	_source: z.string().optional(),
});

/**
 * Custom-tool (custom action) updated payload. Broadcast org-room-wide on every
 * mutation so a second admin's open config page refetches the custom-actions
 * list.
 */
export const CustomToolUpdatedPayloadSchema = z.object({
	operation: z.enum(["created", "updated", "deleted", "toggled"]),
	toolId: z.string(),
	name: z.string(),
	enabled: z.boolean(),
	_source: z.string().optional(),
});

/** Service status payload - broadcasts service health changes */
export const ServiceStatusPayloadSchema = z.object({
	service: z.enum(["database", "redis", "backend"]),
	status: z.enum(["connected", "disconnected", "connecting", "degraded"]),
	reason: z.string().optional(),
	message: z.string().optional(),
	attemptNumber: z.number().optional(),
	retryInSeconds: z.number().optional(),
	_source: z.string().optional(),
});

/** Metrics updated payload */
export const MetricsUpdatedPayloadSchema = z.record(z.string(), z.unknown());

/** Snapshot payload */
export const SnapshotPayloadSchema = z.record(z.string(), z.unknown());

export const WhatsAppTemplateStatusPayloadSchema = z.object({
	orgId: z.string(),
	name: z.string(),
	language: z.string(),
	metaTemplateId: z.string().nullish(),
	status: z.enum([
		"PENDING",
		"APPROVED",
		"REJECTED",
		"PAUSED",
		"DISABLED",
		"PENDING_DELETION",
		"DELETED",
	]),
	reason: z.string().nullish(),
	category: z.string(),
	qualityScore: z.string().nullish(),
});

export const CampaignUpdatedPayloadSchema = z.discriminatedUnion("operation", [
	z.object({
		campaignId: z.string(),
		operation: z.literal("deleted"),
	}),
	z.object({
		campaignId: z.string(),
		operation: z.literal("campaign_status"),
		status: z.enum(["DRAFT", "SENDING", "COMPLETED", "FAILED", "CANCELLED"]),
	}),
	z.object({
		at: z.string().datetime(),
		campaignId: z.string(),
		operation: z.literal("recipient_status"),
		recipientId: z.string(),
		status: z.enum([
			"QUEUED",
			"SENDING",
			"SENT",
			"DELIVERED",
			"READ",
			"REPLIED",
			"FAILED",
		]),
	}),
	z.object({
		campaignId: z.string(),
		counters: z.object({
			delivered: z.number().int().nonnegative(),
			failed: z.number().int().nonnegative(),
			queued: z.number().int().nonnegative(),
			read: z.number().int().nonnegative(),
			replied: z.number().int().nonnegative(),
			sending: z.number().int().nonnegative(),
			sent: z.number().int().nonnegative(),
		}),
		operation: z.literal("counters"),
	}),
]);

export const WaitlistUpdatedPayloadSchema = z.object({
	id: z.string(),
	operation: z.string(),
});

/**
 * Onboarding waiting-users queue changed — a user joined or left the
 * awaiting-org-assignment queue, or a superadmin assigned one to an org.
 * Platform-global system event with a deliberately content-free payload;
 * the superuser Waiting Users page refetches its list on receipt. NOT the
 * booking waitlist (`waitlist_updated`).
 */
export const WaitingUsersUpdatedPayloadSchema = z.object({
	operation: z.enum(["joined", "left", "assigned"]),
	updated_at: z.string(),
	_source: z.string().optional(),
});

/**
 * Q&A pair mutated (training-sources plan §6.2). Org-room event: the config
 * page refetches the Q&A list on receipt. `qaPairId` is `null` for a bulk
 * delete (no single target).
 */
export const QaPairUpdatedPayloadSchema = z.object({
	operation: z.enum(["create", "update", "delete", "bulk_delete"]),
	qaPairId: z.string().nullable(),
	_source: z.string().optional(),
});

/**
 * Data-export request lifecycle changed (RUNNING / READY / FAILED). Delivered
 * only on the requesting user's user-scoped channel; the privacy tab refetches
 * its export list on receipt instead of polling.
 */
export const DataExportUpdatedPayloadSchema = z.object({
	exportRequestId: z.string(),
	status: z.enum(["PENDING", "RUNNING", "READY", "EXPIRED", "FAILED"]),
	_source: z.string().optional(),
});

/**
 * Billing state changed for an organization. Consumers refetch the canonical
 * billing-status query on receipt; the payload stays intentionally small.
 */
export const BillingStatusUpdatedPayloadSchema = z.object({
	orgId: z.string().min(1),
	subscriptionStatus: SubscriptionStatusSchema,
	_source: z.string().optional(),
});

/**
 * Org activated — a superuser flipped the org-active toggle on. Delivered
 * only on each member's user-scoped channel. On receipt the client
 * dispatches the `session:refresh` window event so the pending-org page
 * forwards into the app instead of polling.
 */
export const OrgActivatedPayloadSchema = z.object({
	orgId: z.string(),
	_source: z.string().optional(),
});

/**
 * Org assigned — a superuser attached a waiting (org-less) user to an org.
 * Delivered only on the affected user's user-scoped channel. On receipt the
 * client dispatches the `session:refresh` window event to pick up the new
 * membership.
 */
export const OrgAssignedPayloadSchema = z.object({
	orgId: z.string(),
	_source: z.string().optional(),
});

/**
 * Permissions changed — a role write (create / update / delete) or a
 * membership write (custom role assigned / unassigned, member removed or
 * deactivated) changed who may do what inside ONE org.
 *
 * TENANT-scoped: it arrives on every member's stream, so the receiver must
 * decide for ITSELF whether it is affected — its own user id in
 * `affectedUserIds`, its own `Member` row id in `affectedMemberIds`, or the
 * role it holds in `roleId`. On a match the client dispatches the
 * `session:refresh` window event to re-enrich `user.permissions`; otherwise
 * it ignores the frame.
 *
 * Both id lists are optional because the server producers only carry what
 * they already loaded: the role writes know `roleId` but never enumerate the
 * members holding it, the assign/unassign writes gate on the membership row
 * (no userId), and the team writes know the userId but no role.
 */
export const PermissionsChangedPayloadSchema = z.object({
	orgId: z.string().min(1),
	/**
	 * WHAT changed, for clients that branch coarsely: which role a member
	 * holds, the grants inside a role, or the membership itself.
	 */
	change: z.enum(["role_assignment", "permission_set", "membership"]),
	/** The producing operation — the fine-grained detail behind `change`. */
	reason: z.enum([
		"role_created",
		"role_updated",
		"role_deleted",
		"role_assigned",
		"role_unassigned",
		"member_created",
		"member_updated",
		"member_removed",
		"member_active_changed",
	]),
	/** The written / assigned role; absent when there is no single one. */
	roleId: z.string().min(1).nullish(),
	/** Affected user ids — what the team (membership) writes have in hand. */
	affectedUserIds: z.array(z.string()).optional(),
	/** Affected `Member` row ids — what the roles writes have in hand. */
	affectedMemberIds: z.array(z.string()).optional(),
	_source: z.string().nullish(),
	/** Acting org member's user id (absent for system flows). */
	actor_user_id: z.string().nullish(),
	/** Acting org member's display label (email today). */
	actor_name: z.string().nullish(),
});

/**
 * Org scope changed — a superadmin entered or exited org impersonation.
 * Delivered ONLY on that superadmin's org-less user-scoped channel, so it
 * reaches their other tabs (whose streams connected under the scope being
 * left) and no one else's. On receipt those tabs dispatch `session:refresh`
 * and re-scope their org-keyed query caches.
 */
export const OrgScopeChangedPayloadSchema = z.object({
	/** `entered` = now acting inside `orgId`; `exited` = back to platform scope. */
	action: z.enum(["entered", "exited"]),
	/** The org entered; null on exit. */
	orgId: z.string().nullable(),
	orgName: z.string().nullish(),
	impersonationId: z.string().nullish(),
	/** The superadmin whose scope changed — i.e. the recipient. */
	userId: z.string().min(1).nullish(),
	_source: z.string().optional(),
});

// =============================================================================
// RAG Progress Event Payload Schemas
// =============================================================================

/**
 * RAG progress stage enum.
 *
 * Widened past the original parse/chunk/embed/store quartet to name the steps a
 * real ingest actually runs — a .docx/.xlsx spends most of its wall clock in
 * them and used to report them all as "parsing":
 * - `analyzing`       — LLM sheet analysis; runs BEFORE the parse (spreadsheets).
 * - `figures`         — figure describe / OCR enrichment (WS11).
 * - `summarizing`     — one-paragraph document summary (WS1.3).
 * - `contextualizing` — contextual-embedding prefix generation.
 *
 * Purely additive: the original six values are unchanged and keep their
 * meaning, so an un-upgraded consumer still parses every event it used to.
 */
export const RagProgressStageSchema = z.enum([
	"analyzing",
	"parsing",
	"figures",
	"summarizing",
	"chunking",
	"contextualizing",
	"embedding",
	"storing",
	"completed",
	"failed",
]);

/**
 * Canonical stepper order — the WORKING stages in the order
 * `RagPipelineService.process()` runs them. Exported so a UI renders its
 * stepper off this array instead of hardcoding (and re-deriving) the order.
 *
 * Not every stage fires for every document, and a step must be rendered as
 * live ONLY once its event actually arrives: `analyzing` is spreadsheet-only,
 * `figures` only when the parse found figures, `summarizing` only when a
 * metadata extractor is configured, and `contextualizing` only when the
 * contextual strategy is active AND a job enqueuer is bound — which it is NOT
 * in local dev (`SKIP_BACKGROUND_WORKERS=true` leaves the enqueuer unbound and
 * contextual silently degrades to standard), so that stage never lights up on
 * a dev machine.
 */
export const RAG_PROGRESS_STAGE_ORDER = [
	"analyzing",
	"parsing",
	"figures",
	"summarizing",
	"chunking",
	"contextualizing",
	"embedding",
	"storing",
] as const satisfies readonly RagProgressStage[];

/** Terminal stages — outcomes, not steps; never members of the stepper. */
export const RAG_TERMINAL_PROGRESS_STAGES = [
	"completed",
	"failed",
] as const satisfies readonly RagProgressStage[];

/**
 * Per-item sub-step counter for stages that iterate (figure describe/OCR
 * today). `total` is how many items this stage will touch, `done` how many are
 * finished. Optional everywhere — a stage that does not iterate omits it
 * rather than reporting a fabricated 1-of-1.
 */
export const RagProgressSubStepSchema = z.object({
	done: z.number().int().nonnegative(),
	total: z.number().int().nonnegative(),
	/** What is being counted (e.g. 'figure'). UI label only. */
	label: z.string().optional(),
});

/**
 * How this document was parsed.
 *
 * `local` is the self-hosted docling container: it costs nothing, so a UI may
 * say "Local Docling — free" / $0.00 with a straight face. `managed` is the
 * hosted watsonx parser, whose per-document price is not reported by any API
 * this repo calls — a managed parse therefore contributes an `unknown` cost
 * entry, or an explicitly `estimated` one, and never a bare number.
 */
export const RagParseModeSchema = z.enum(["local", "managed"]);

/**
 * Provenance of a single cost figure. A first-class discriminant so an
 * unknowable cost cannot collapse into a silent `0`:
 * - `actual`    — the provider reported this number; safe to bill against.
 * - `estimated` — derived locally (token count x price table). Never summed
 *                 into the actual total. Embedding spend is estimate-only
 *                 forever: the /embeddings endpoint returns no cost.
 * - `unknown`   — not knowable from anything this repo can observe. `usd` is
 *                 `null`, and the entry has no numeric slot to fall into.
 */
export const RagCostSourceSchema = z.enum(["actual", "estimated", "unknown"]);

/** Fields shared by every cost-breakdown member. */
const RagCostBreakdownBaseShape = {
	/** Stage that incurred the cost. */
	stage: RagProgressStageSchema,
	/** Billed model, when a model was involved. */
	model: z.string().optional(),
	/** Human detail (e.g. 'Local Docling — free'). UI label only. */
	detail: z.string().optional(),
};

/**
 * One cost contribution. Modelled as a discriminated union rather than a
 * nullable number so `unknown` is representable without ever being mistaken
 * for free: the `unknown` member's `usd` is literally `null`.
 */
export const RagCostBreakdownEntrySchema = z.discriminatedUnion("costSource", [
	z.object({
		...RagCostBreakdownBaseShape,
		costSource: z.literal("actual"),
		usd: z.number().nonnegative(),
	}),
	z.object({
		...RagCostBreakdownBaseShape,
		costSource: z.literal("estimated"),
		usd: z.number().nonnegative(),
	}),
	z.object({
		...RagCostBreakdownBaseShape,
		costSource: z.literal("unknown"),
		usd: z.null(),
	}),
]);

/**
 * Running totals, split by provenance. Deliberately NOT one number — an
 * estimate must never be rendered as a bill. `unknownCount` is how many
 * contributions could not be priced at all; a UI shows that as "unknown",
 * not as zero.
 */
export const RagCostTotalsSchema = z.object({
	/** Sum of every `actual` entry. */
	actualUsd: z.number().nonnegative(),
	/** Sum of every `estimated` entry. Kept apart from `actualUsd`. */
	estimatedUsd: z.number().nonnegative(),
	/** How many entries carry `costSource: 'unknown'` (`usd === null`). */
	unknownCount: z.number().int().nonnegative(),
});

/** Cost channel carried alongside progress: running totals + their sources. */
export const RagProgressCostSchema = z.object({
	totals: RagCostTotalsSchema,
	breakdown: z.array(RagCostBreakdownEntrySchema),
});

/**
 * RAG progress event payload.
 *
 * Every field added after the original six is optional, so an un-upgraded
 * consumer still parses an upgraded event (and an un-upgraded emitter still
 * produces a valid one).
 */
export const RagProgressPayloadSchema = z.object({
	documentId: z.string(),
	stage: RagProgressStageSchema,
	progress: z.number().min(0).max(100),
	message: z.string().optional(),
	error: z.string().optional(),
	chunkCount: z.number().optional(),
	/** Per-item counter for iterating stages (figures). */
	subStep: RagProgressSubStepSchema.optional(),
	/** How the document was parsed — drives the "Local Docling — free" label. */
	parseMode: RagParseModeSchema.optional(),
	/** Spend so far, split actual / estimated / unknown. */
	cost: RagProgressCostSchema.optional(),
	_source: z.string().optional(),
});

// =============================================================================
// Discriminated Union Schemas for Subscriptions
// =============================================================================

/** Calendar events union - for calendar subscription streams */
export const CalendarEventSchema = z.discriminatedUnion("event", [
	z.object({
		event: z.literal("event_created"),
		payload: EventCreatedPayloadSchema,
	}),
	z.object({
		event: z.literal("event_updated"),
		payload: EventUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("event_cancelled"),
		payload: EventCancelledPayloadSchema,
	}),
	z.object({
		event: z.literal("event_reinstated"),
		payload: EventReinstatedPayloadSchema,
	}),
	z.object({
		event: z.literal("event_completed"),
		payload: EventCompletedPayloadSchema,
	}),
	z.object({
		event: z.literal("event_no_show"),
		payload: EventNoShowPayloadSchema,
	}),
]);

/** Conversation events union - for chat/conversation subscription streams */
export const ConversationEventSchema = z.discriminatedUnion("event", [
	z.object({
		event: z.literal("conversation_new_message"),
		payload: ConversationNewMessagePayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_message_sync"),
		payload: ConversationMessageSyncPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_read"),
		payload: ConversationReadPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_cleared"),
		payload: ConversationClearedPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_message_edited"),
		payload: ConversationMessageEditedPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_message_status"),
		payload: ConversationMessageStatusPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_message_deleted"),
		payload: ConversationMessageDeletedPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_typing"),
		payload: ConversationTypingPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_ai_toggled"),
		payload: ConversationAiToggledPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_tool_approval_pending"),
		payload: ConversationToolApprovalPendingPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_feedback"),
		payload: ConversationFeedbackPayloadSchema,
	}),
]);

/** Streaming events union - for AI response streaming */
export const StreamingEventSchema = z.discriminatedUnion("event", [
	z.object({
		event: z.literal("conversation_stream_start"),
		payload: ConversationStreamStartPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_stream_chunk"),
		payload: ConversationStreamChunkPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_stream_sse"),
		payload: ConversationStreamSsePayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_stream_reasoning"),
		payload: ConversationStreamReasoningPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_workflow_progress"),
		payload: ConversationWorkflowProgressPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_stream_end"),
		payload: ConversationStreamEndPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_tool_call_started"),
		payload: ConversationToolCallStartedPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_tool_call_completed"),
		payload: ConversationToolCallCompletedPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_tool_approval_requested"),
		payload: ConversationToolApprovalRequestedPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_tool_approval_responded"),
		payload: ConversationToolApprovalRespondedPayloadSchema,
	}),
	z.object({
		event: z.literal("conversation_stream_snapshot"),
		payload: ConversationStreamSnapshotPayloadSchema,
	}),
]);

/** User events union - for user state change streams */
export const UserEventSchema = z.discriminatedUnion("event", [
	z.object({
		event: z.literal("user_document_updated"),
		payload: UserDocumentUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("user_favorite_updated"),
		payload: UserFavoriteUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("user_active_updated"),
		payload: UserActiveUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("user_name_updated"),
		payload: UserNameUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("user_phone_e164_changed"),
		payload: UserPhoneChangedPayloadSchema,
	}),
	z.object({
		event: z.literal("contact_merged"),
		payload: ContactMergedPayloadSchema,
	}),
	z.object({
		event: z.literal("contact_created"),
		payload: ContactCreatedPayloadSchema,
	}),
	z.object({
		event: z.literal("contact_updated"),
		payload: ContactUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("contact_deleted"),
		payload: ContactDeletedPayloadSchema,
	}),
	z.object({
		event: z.literal("contact_restored"),
		payload: ContactRestoredPayloadSchema,
	}),
]);

/** System events union - for system-wide notifications */
export const SystemEventSchema = z.discriminatedUnion("event", [
	z.object({
		event: z.literal("config_updated"),
		payload: ConfigUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("ai_preset_updated"),
		payload: AiPresetUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("vacation_period_updated"),
		payload: VacationPeriodUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("custom_tool_updated"),
		payload: CustomToolUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("service_status"),
		payload: ServiceStatusPayloadSchema,
	}),
	z.object({
		event: z.literal("metrics_updated"),
		payload: MetricsUpdatedPayloadSchema,
	}),
	z.object({ event: z.literal("snapshot"), payload: SnapshotPayloadSchema }),
	z.object({
		event: z.literal("whatsapp_template_status"),
		payload: WhatsAppTemplateStatusPayloadSchema,
	}),
	z.object({
		event: z.literal("campaign_updated"),
		payload: CampaignUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("waitlist_updated"),
		payload: WaitlistUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("waiting_users_updated"),
		payload: WaitingUsersUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("qa_pair_updated"),
		payload: QaPairUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("billing_status_updated"),
		payload: BillingStatusUpdatedPayloadSchema,
	}),
	z.object({
		event: z.literal("permissions_changed"),
		payload: PermissionsChangedPayloadSchema,
	}),
]);

/** RAG progress events - for document processing progress */
export const RagProgressEventSchema = z.object({
	event: z.literal("rag_progress"),
	payload: RagProgressPayloadSchema,
});

/** Data-export lifecycle event - delivered on the requester's user-scoped channel */
export const DataExportUpdatedEventSchema = z.object({
	event: z.literal("data_export_updated"),
	payload: DataExportUpdatedPayloadSchema,
});

/** Org-activated lifecycle event - delivered on each member's user-scoped channel */
export const OrgActivatedEventSchema = z.object({
	event: z.literal("org_activated"),
	payload: OrgActivatedPayloadSchema,
});

/** Org-assigned lifecycle event - delivered on the assigned user's user-scoped channel */
export const OrgAssignedEventSchema = z.object({
	event: z.literal("org_assigned"),
	payload: OrgAssignedPayloadSchema,
});

/**
 * Org-scope-changed event - delivered on the acting superadmin's org-less
 * user-scoped channel so their OTHER tabs react to the impersonation
 * enter/exit.
 */
export const OrgScopeChangedEventSchema = z.object({
	event: z.literal("org_scope_changed"),
	payload: OrgScopeChangedPayloadSchema,
});

/** Combined chat event - includes conversation, streaming, and user events for a conversation */
export const ChatEventSchema = z.union([
	ConversationEventSchema,
	StreamingEventSchema,
	UserEventSchema,
]);

/** All realtime events - complete union of all event types */
export const RealtimeEventSchema = z.union([
	CalendarEventSchema,
	ConversationEventSchema,
	StreamingEventSchema,
	UserEventSchema,
	SystemEventSchema,
	RagProgressEventSchema,
	DataExportUpdatedEventSchema,
	OrgActivatedEventSchema,
	OrgAssignedEventSchema,
	OrgScopeChangedEventSchema,
]);

// =============================================================================
// Contract Definition - SSE Endpoints (HTTP GET)
// =============================================================================

/**
 * SSE-based subscriptions contract
 *
 * These endpoints define the subscription contract used by both transports:
 * - SSE (HTTP GET) via NestJS SubscriptionsProcedures
 * - WebSocket via RPCHandler in orpc-ws-server.ts (same contract, same async generators)
 */
export const subscriptionsContract = {
	/**
	 * Subscribe to calendar events (SSE).
	 *
	 * Streams calendar event changes including:
	 * - event_created: New events scheduled
	 * - event_updated: Events modified
	 * - event_cancelled: Events cancelled
	 * - event_reinstated: Cancelled events restored
	 */
	calendar: oc
		.route({
			method: "GET",
			path: "/api/subscribe/calendar",
			summary: "Subscribe to calendar events (SSE)",
			description:
				"Server-Sent Events stream for calendar event changes. " +
				"Includes event creation, updates, cancellation, and reinstatement.",
			tags: ["Subscriptions"],
		})
		.input(
			z
				.object({
					fromDate: z.string().datetime().optional(),
					toDate: z.string().datetime().optional(),
				})
				.optional()
		)
		.output(eventIterator(CalendarEventSchema)),

	/**
	 * Subscribe to AI streaming events (SSE).
	 *
	 * Dedicated stream for AI streaming responses:
	 * - stream_start/end: Stream lifecycle
	 * - stream_chunk: Text deltas
	 * - stream_sse: SSE-formatted chunks
	 * - stream_reasoning: Thinking deltas
	 * - tool_call_started/completed: Tool execution
	 * - tool_approval_requested: Tool approval UI
	 */
	streaming: oc
		.route({
			method: "GET",
			path: "/api/subscribe/streaming/{phoneE164}",
			summary: "Subscribe to AI streaming events (SSE)",
			description:
				"Server-Sent Events stream for AI assistant streaming responses. " +
				"Includes stream chunks, reasoning, and tool calls.",
			tags: ["Subscriptions"],
		})
		.input(
			z.object({
				phoneE164: PhoneE164Schema,
			})
		)
		.output(eventIterator(StreamingEventSchema)),

	/**
	 * Subscribe to conversation/chat events (SSE).
	 *
	 * Streams all conversation-related events for a specific phone number:
	 * - Messages (new, sync, edited)
	 * - Typing indicators
	 * - AI streaming responses
	 * - Tool calls
	 * - User state changes (for this conversation)
	 */
	chat: oc
		.route({
			method: "GET",
			path: "/api/subscribe/chat/{phoneE164}",
			summary: "Subscribe to conversation events (SSE)",
			description:
				"Server-Sent Events stream for conversation events. " +
				"Includes messages, typing indicators, AI streaming, and tool calls.",
			tags: ["Subscriptions"],
		})
		.input(
			z.object({
				phoneE164: PhoneE164Schema,
			})
		)
		.output(eventIterator(ChatEventSchema)),

	/**
	 * Subscribe to system events (SSE).
	 *
	 * Streams system-wide events:
	 * - Config changes
	 * - Service status updates
	 * - Vacation period updates
	 * - Metrics
	 */
	system: oc
		.route({
			method: "GET",
			path: "/api/subscribe/system",
			summary: "Subscribe to system events (SSE)",
			description:
				"Server-Sent Events stream for system-wide events. " +
				"Includes config changes, service status, and metrics.",
			tags: ["Subscriptions"],
		})
		.input(z.object({}).optional())
		.output(eventIterator(SystemEventSchema)),

	/**
	 * Subscribe to all realtime events (SSE).
	 *
	 * Unified stream of all realtime events. Use event type filtering
	 * on the client side to handle specific events.
	 */
	all: oc
		.route({
			method: "GET",
			path: "/api/subscribe/all",
			summary: "Subscribe to all realtime events (SSE)",
			description:
				"Server-Sent Events stream for all realtime events. " +
				"Provides a unified stream for clients that need comprehensive updates.",
			tags: ["Subscriptions"],
		})
		.input(
			z
				.object({
					/** Filter by event types (comma-separated) */
					types: z.string().optional(),
					/** Filter by entity identifiers (comma-separated) */
					entities: z.string().optional(),
				})
				.optional()
		)
		.output(eventIterator(RealtimeEventSchema)),

	/**
	 * Subscribe to RAG progress events (SSE).
	 *
	 * Streams RAG document processing progress. Working stages, in canonical
	 * order (`RAG_PROGRESS_STAGE_ORDER`) — several are conditional and simply
	 * never arrive for a given document:
	 * - analyzing: Spreadsheet sheets are being analyzed (pre-parse)
	 * - parsing: Document is being parsed
	 * - figures: Figures are being described / OCR'd
	 * - summarizing: A document summary is being extracted
	 * - chunking: Document is being split into chunks
	 * - contextualizing: Contextual-embedding prefixes are being generated
	 * - embedding: Chunks are being embedded
	 * - storing: Embeddings are being stored
	 *
	 * Terminal stages (`RAG_TERMINAL_PROGRESS_STAGES`):
	 * - completed: Processing finished successfully
	 * - failed: Processing failed with error
	 *
	 * Progress is ephemeral — there is no stage/progress column, so a reload
	 * mid-ingest loses the stream's history.
	 */
	rag: oc
		.route({
			method: "GET",
			path: "/api/subscribe/rag",
			summary: "Subscribe to RAG progress events (SSE)",
			description:
				"Server-Sent Events stream for RAG document processing progress. " +
				"Provides real-time updates on document parsing, chunking, embedding, and storage.",
			tags: ["Subscriptions"],
		})
		.input(z.object({}).optional())
		.output(eventIterator(RagProgressEventSchema)),
};

// =============================================================================
// Type Exports
// =============================================================================

export type SubscriptionsContract = typeof subscriptionsContract;
// Payload types
export type EventCreatedPayload = z.infer<typeof EventCreatedPayloadSchema>;
export type EventUpdatedPayload = z.infer<typeof EventUpdatedPayloadSchema>;
export type EventCancelledPayload = z.infer<typeof EventCancelledPayloadSchema>;
export type EventReinstatedPayload = z.infer<
	typeof EventReinstatedPayloadSchema
>;
export type EventCompletedPayload = z.infer<typeof EventCompletedPayloadSchema>;
export type EventNoShowPayload = z.infer<typeof EventNoShowPayloadSchema>;

export type ConversationNewMessagePayload = z.infer<
	typeof ConversationNewMessagePayloadSchema
>;
export type ConversationMessageSyncPayload = z.infer<
	typeof ConversationMessageSyncPayloadSchema
>;
export type ConversationReadPayload = z.infer<
	typeof ConversationReadPayloadSchema
>;
export type ConversationClearedPayload = z.infer<
	typeof ConversationClearedPayloadSchema
>;
export type ConversationMessageEditedPayload = z.infer<
	typeof ConversationMessageEditedPayloadSchema
>;
export type ConversationMessageStatusPayload = z.infer<
	typeof ConversationMessageStatusPayloadSchema
>;
export type ConversationMessageDeletedPayload = z.infer<
	typeof ConversationMessageDeletedPayloadSchema
>;
export type ConversationTypingPayload = z.infer<
	typeof ConversationTypingPayloadSchema
>;
export type ConversationAiToggledPayload = z.infer<
	typeof ConversationAiToggledPayloadSchema
>;
export type ConversationToolApprovalPendingPayload = z.infer<
	typeof ConversationToolApprovalPendingPayloadSchema
>;
export type ConversationFeedbackPayload = z.infer<
	typeof ConversationFeedbackPayloadSchema
>;

export type ConversationStreamStartPayload = z.infer<
	typeof ConversationStreamStartPayloadSchema
>;
export type ConversationStreamChunkPayload = z.infer<
	typeof ConversationStreamChunkPayloadSchema
>;
export type ConversationStreamSsePayload = z.infer<
	typeof ConversationStreamSsePayloadSchema
>;
export type ConversationStreamReasoningPayload = z.infer<
	typeof ConversationStreamReasoningPayloadSchema
>;
export type ConversationStreamEndPayload = z.infer<
	typeof ConversationStreamEndPayloadSchema
>;
export type ConversationToolCallStartedPayload = z.infer<
	typeof ConversationToolCallStartedPayloadSchema
>;
export type ConversationToolCallCompletedPayload = z.infer<
	typeof ConversationToolCallCompletedPayloadSchema
>;
export type ConversationToolApprovalRequestedPayload = z.infer<
	typeof ConversationToolApprovalRequestedPayloadSchema
>;
export type ConversationToolApprovalRespondedPayload = z.infer<
	typeof ConversationToolApprovalRespondedPayloadSchema
>;
export type ConversationStreamSnapshotPayload = z.infer<
	typeof ConversationStreamSnapshotPayloadSchema
>;

export type UserDocumentUpdatedPayload = z.infer<
	typeof UserDocumentUpdatedPayloadSchema
>;
export type UserFavoriteUpdatedPayload = z.infer<
	typeof UserFavoriteUpdatedPayloadSchema
>;
export type UserActiveUpdatedPayload = z.infer<
	typeof UserActiveUpdatedPayloadSchema
>;
export type UserNameUpdatedPayload = z.infer<
	typeof UserNameUpdatedPayloadSchema
>;
export type UserPhoneChangedPayload = z.infer<
	typeof UserPhoneChangedPayloadSchema
>;
export type ContactMergedPayload = z.infer<typeof ContactMergedPayloadSchema>;
export type ContactCreatedPayload = z.infer<typeof ContactCreatedPayloadSchema>;
export type ContactUpdatedPayload = z.infer<typeof ContactUpdatedPayloadSchema>;
export type ContactDeletedPayload = z.infer<typeof ContactDeletedPayloadSchema>;
export type ContactRestoredPayload = z.infer<
	typeof ContactRestoredPayloadSchema
>;

export type ConfigUpdatedPayload = z.infer<typeof ConfigUpdatedPayloadSchema>;
export type AiPresetUpdatedPayload = z.infer<
	typeof AiPresetUpdatedPayloadSchema
>;
export type VacationPeriodUpdatedPayload = z.infer<
	typeof VacationPeriodUpdatedPayloadSchema
>;
export type ServiceStatusPayload = z.infer<typeof ServiceStatusPayloadSchema>;
export type MetricsUpdatedPayload = z.infer<typeof MetricsUpdatedPayloadSchema>;
export type SnapshotPayload = z.infer<typeof SnapshotPayloadSchema>;
export type WhatsAppTemplateStatusPayload = z.infer<
	typeof WhatsAppTemplateStatusPayloadSchema
>;
export type CampaignUpdatedPayload = z.infer<
	typeof CampaignUpdatedPayloadSchema
>;
export type WaitlistUpdatedPayload = z.infer<
	typeof WaitlistUpdatedPayloadSchema
>;
export type WaitingUsersUpdatedPayload = z.infer<
	typeof WaitingUsersUpdatedPayloadSchema
>;
export type QaPairUpdatedPayload = z.infer<typeof QaPairUpdatedPayloadSchema>;
export type DataExportUpdatedPayload = z.infer<
	typeof DataExportUpdatedPayloadSchema
>;
export type BillingStatusUpdatedPayload = z.infer<
	typeof BillingStatusUpdatedPayloadSchema
>;
export type DataExportUpdatedEvent = z.infer<
	typeof DataExportUpdatedEventSchema
>;
export type OrgActivatedPayload = z.infer<typeof OrgActivatedPayloadSchema>;
export type OrgActivatedEvent = z.infer<typeof OrgActivatedEventSchema>;
export type OrgAssignedPayload = z.infer<typeof OrgAssignedPayloadSchema>;
export type OrgAssignedEvent = z.infer<typeof OrgAssignedEventSchema>;
export type OrgScopeChangedPayload = z.infer<
	typeof OrgScopeChangedPayloadSchema
>;
export type OrgScopeChangedEvent = z.infer<typeof OrgScopeChangedEventSchema>;
export type PermissionsChangedPayload = z.infer<
	typeof PermissionsChangedPayloadSchema
>;
export type AiUsageCostData = z.infer<typeof AiUsageCostDataSchema>;
export type RagProgressStage = z.infer<typeof RagProgressStageSchema>;
export type RagProgressSubStep = z.infer<typeof RagProgressSubStepSchema>;
export type RagParseMode = z.infer<typeof RagParseModeSchema>;
export type RagCostSource = z.infer<typeof RagCostSourceSchema>;
export type RagCostBreakdownEntry = z.infer<typeof RagCostBreakdownEntrySchema>;
export type RagCostTotals = z.infer<typeof RagCostTotalsSchema>;
export type RagProgressCost = z.infer<typeof RagProgressCostSchema>;
export type RagProgressPayload = z.infer<typeof RagProgressPayloadSchema>;
export type RagProgressEvent = z.infer<typeof RagProgressEventSchema>;

// Event union types
export type CalendarEvent = z.infer<typeof CalendarEventSchema>;
export type ConversationEvent = z.infer<typeof ConversationEventSchema>;
export type StreamingEvent = z.infer<typeof StreamingEventSchema>;
export type UserEvent = z.infer<typeof UserEventSchema>;
export type SystemEvent = z.infer<typeof SystemEventSchema>;
export type ChatEvent = z.infer<typeof ChatEventSchema>;
export type RealtimeEvent = z.infer<typeof RealtimeEventSchema>;

import type { InferContractRouterInputs } from "@orpc/contract";

// Contract input types using oRPC inference utilities
type SubscriptionsInputs = InferContractRouterInputs<SubscriptionsContract>;

// Input types inferred from contract
export type CalendarSubscriptionInput = SubscriptionsInputs["calendar"];
export type StreamingSubscriptionInput = SubscriptionsInputs["streaming"];
export type ChatSubscriptionInput = SubscriptionsInputs["chat"];
export type SystemSubscriptionInput = SubscriptionsInputs["system"];
export type AllSubscriptionInput = SubscriptionsInputs["all"];
export type RagSubscriptionInput = SubscriptionsInputs["rag"];
